import { useEffect, useState } from 'react';
import { supabase } from './supabase';
import { SUPPLEMENTARY_FORM_CODES_SQL } from './formCodes';
import { checklistForVisa } from './documentChecklist';
import { pickFormId, hasSponsorForm, isSponsorRequired, sponsorRoleNoun } from './schema-runtime';

/**
 * Department form number quoted in the second-party timeline copy. The 600
 * tourist Inviter form is ADI's own, so it has no Department number and
 * returns null (the parenthetical is then suppressed).
 */
function sponsorFormNumberFor(visaSubclass: string, variant: string | null): string | null {
  const formId = pickFormId(visaSubclass, true, variant);
  if (formId === '600_tourist_inviter') return null;
  if (formId === '1149_family_visitor_sponsor') return '1149';
  return '40SP';
}
import { computeTimelineSteps, type TimelineContext, type TimelineStep } from './matterTimeline';

interface MatterMinimal {
  status: string;
  initial_payment_date: string | null;
  final_payment_date: string | null;
  created_at?: string | null;
}

/**
 * Single-source-of-truth hook used by every matter view (Dashboard,
 * MatterOverview, LodgementGate). Fetches the matter context — payments,
 * intake form completion, doc roll-ups — and returns the computed timeline
 * steps. Re-runs when reloadKey is bumped.
 */
export function useMatterTimeline(args: {
  matterId: string;
  visaSubclass: string;
  variant: string | null;
  applicantContactId: string;
  sponsorContactId: string | null;
  hasMinorDependents?: boolean;
  /** Pre-supplied matter row (saves a fetch when the parent already has one). */
  matter?: MatterMinimal | null;
  reloadKey?: number;
}): { steps: TimelineStep[]; loading: boolean; ctx: TimelineContext | null } {
  const [steps, setSteps] = useState<TimelineStep[]>([]);
  const [ctx, setCtx] = useState<TimelineContext | null>(null);
  const [loading, setLoading] = useState(true);
  const sponsorIntakeRequired = hasSponsorForm(args.visaSubclass) && !!args.sponsorContactId;
  // The 600-tourist Inviter form is ADI's own and is never lodged with the
  // Department, so an unfinished one must never hold up lodgement. Real sponsor
  // forms (40SP, 1149) still do. Tzvi ruling 2026-07-24.
  const sponsorIntakeBlocksLodgement = sponsorIntakeRequired && isSponsorRequired(args.visaSubclass);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);

      // Pull the matter row if not supplied.
      let matter: MatterMinimal | null | undefined = args.matter;
      if (!matter) {
        const { data } = await supabase
          .from('matters')
          .select('status, initial_payment_date, final_payment_date, created_at')
          .eq('id', args.matterId)
          .maybeSingle();
        matter = (data as MatterMinimal | null) ?? null;
      }
      if (cancelled || !matter) {
        setLoading(false);
        return;
      }

      // Required-doc roll-ups + intake-form status in parallel.
      const intakeContactIds = [args.applicantContactId];
      if (sponsorIntakeRequired && args.sponsorContactId) intakeContactIds.push(args.sponsorContactId);

      const [docsRes, intakeRes] = await Promise.all([
        supabase
          .from('documents')
          .select('id, checklist_item, review_status, previous_version_id')
          .eq('matter_id', args.matterId)
          .is('deleted_at', null),
        supabase
          .from('intake_forms')
          .select('contact_id, status, completed_at, last_saved_at')
          .eq('matter_id', args.matterId)
          .in('contact_id', intakeContactIds)
          .not('form_code', 'in', SUPPLEMENTARY_FORM_CODES_SQL)
          .order('last_saved_at', { ascending: false }),
      ]);
      if (cancelled) return;

      const checklist = checklistForVisa(args.visaSubclass, args.variant, {
        hasMinorDependents: !!args.hasMinorDependents,
        // Keep this required-doc roll-up counting the same rows the Documents
        // page renders: a linked 600-tourist Inviter adds the inviter document
        // set there, so it must add it here too or the counts disagree (the
        // B1b/B12 single-source-of-truth invariant).
        hasInviterParty: !!args.sponsorContactId,
      });
      const required = checklist.filter((c) => c.required);
      const docRows = (docsRes.data ?? []) as Array<{ id: string; checklist_item: string | null; review_status: string; previous_version_id: string | null }>;
      const supersededIds = new Set<string>();
      for (const r of docRows) if (r.previous_version_id) supersededIds.add(r.previous_version_id);
      const active = docRows.filter((r) => !supersededIds.has(r.id));
      const uploadedKeys = new Set(active.filter((r) => r.checklist_item).map((r) => r.checklist_item as string));
      const approvedKeys = new Set(active.filter((r) => r.review_status === 'approved').map((r) => r.checklist_item as string));

      const intakeRows = (intakeRes.data ?? []) as Array<{ contact_id: string; status: string; completed_at: string | null }>;
      const seen = new Set<string>();
      let appStatus: string | null = null;
      let appCompletedAt: string | null = null;
      let spStatus: string | null = null;
      let spCompletedAt: string | null = null;
      for (const r of intakeRows) {
        if (seen.has(r.contact_id)) continue;
        seen.add(r.contact_id);
        if (r.contact_id === args.applicantContactId) {
          appStatus = r.status;
          appCompletedAt = r.completed_at;
        } else if (r.contact_id === args.sponsorContactId) {
          spStatus = r.status;
          spCompletedAt = r.completed_at;
        }
      }

      const context: TimelineContext = {
        matter,
        applicantIntakeStatus: appStatus,
        applicantIntakeCompletedAt: appCompletedAt,
        sponsorIntakeRequired,
        sponsorIntakeBlocksLodgement,
        sponsorFormNumber: sponsorFormNumberFor(args.visaSubclass, args.variant),
        sponsorRoleNoun: sponsorRoleNoun(args.visaSubclass),
        sponsorIntakeStatus: spStatus,
        sponsorIntakeCompletedAt: spCompletedAt,
        requiredDocsTotal: required.length,
        requiredDocsUploaded: required.filter((c) => uploadedKeys.has(c.key)).length,
        requiredDocsApproved: required.filter((c) => approvedKeys.has(c.key)).length,
      };

      setCtx(context);
      setSteps(computeTimelineSteps(context));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [args.matterId, args.visaSubclass, args.variant, args.applicantContactId, args.sponsorContactId, args.hasMinorDependents, args.matter, args.reloadKey, sponsorIntakeRequired, sponsorIntakeBlocksLodgement]);

  return { steps, loading, ctx };
}
